import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useNotification } from '@/components/NotificationProvider';
import { usePermissions } from '@/components/PermissionsProvider';
import { MoreHorizontal, Trash2, Plus, Mail, Send, Grid3X3, Users as UsersIcon, Check, X as XIcon, Eye, Pencil, Save, RotateCcw, History, Ban, KeyRound, ShieldCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RBAC_ROLES = [
  "Super-User", "Management", "Personnel DB Manager", "Equipment DB Manager",
  "Forecaster (Sales)", "Personnel Planner", "Equipment Planner", "Controlling",
  "Engineer", "HR", "Travel Coordinator", "Maintenance Coordinator", "Project Admin"
];

function getAuthHeader() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

export default function Users({ user }) {
  const notification = useNotification();
  const { canEdit, userRoles, loading: permsLoading, refresh: refreshPermissions } = usePermissions();
  const [users, setUsers] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', role: 'Engineer', roles: [] });

  // Access Rights Matrix state (loaded from backend)
  const [matrixData, setMatrixData] = useState(null); // { matrix, roles, modules }
  const [matrixDraft, setMatrixDraft] = useState(null); // local edits
  const [matrixDirty, setMatrixDirty] = useState(false);
  const [matrixSaving, setMatrixSaving] = useState(false);
  const [showAuditDialog, setShowAuditDialog] = useState(false);
  const [auditEntries, setAuditEntries] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  // Security (auth) log state
  const [secLog, setSecLog] = useState({ entries: [], summary: null });
  const [secLogLoading, setSecLogLoading] = useState(false);
  const [secLogFilter, setSecLogFilter] = useState('all'); // all | login_success | login_failed | account_locked
  const isSuperUser = (userRoles || []).some(r => r === 'Super-User' || r === 'Admin');

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/users`, { headers: getAuthHeader() });
      setUsers(response.data);
    } catch (error) {
      if (error.response?.status !== 403) {
        notification.error('Failed to load users');
      }
    }
  }, [notification]);

  const fetchAccessMatrix = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/access-matrix`, { headers: getAuthHeader() });
      setMatrixData(res.data);
      setMatrixDraft(JSON.parse(JSON.stringify(res.data.matrix || {})));
      setMatrixDirty(false);
    } catch (error) {
      if (error.response?.status !== 403) {
        notification.error('Failed to load access matrix');
      }
    }
  }, [notification]);

  useEffect(() => {
    if (permsLoading || !canEdit('user_management')) return;
    fetchUsers();
    fetchAccessMatrix();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchUsers, fetchAccessMatrix, permsLoading]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await axios.post(`${API}/auth/register`, {
        email: newUser.email.toLowerCase(),
        role: (newUser.roles || []).length > 0 ? newUser.roles[0] : 'Engineer',
        roles: newUser.roles || []
      }, { headers: getAuthHeader() });
      notification.success(`Invitation sent to ${newUser.email}`);
      setShowCreateDialog(false);
      setNewUser({ email: '', role: 'Engineer', roles: [] });
      fetchUsers();
    } catch (error) {
      notification.error(error.response?.data?.detail || 'Failed to invite user');
    } finally {
      setCreating(false);
    }
  };

  const handleResendInvite = async (userId, email) => {
    try {
      await axios.post(`${API}/auth/resend-invite?user_id=${userId}`, {}, { headers: getAuthHeader() });
      notification.success(`Invitation resent to ${email}`);
    } catch (error) {
      notification.error(error.response?.data?.detail || 'Failed to resend invitation');
    }
  };

  const handleDelete = (userId) => {
    notification.confirm('Are you sure you want to delete this user?', async () => {
      try {
        await axios.delete(`${API}/users/${userId}`, { headers: getAuthHeader() });
        notification.success('User deleted successfully');
        fetchUsers();
      } catch (error) {
        notification.error('Failed to delete user');
      }
    });
  };

  const handleDisable = (userObj) => {
    notification.confirm(
      `Disable ${userObj.email}?\n\nThey will not be able to log in until re-enabled. Their data is preserved.`,
      async () => {
        try {
          await axios.post(`${API}/users/${userObj.id}/disable`, {}, { headers: getAuthHeader() });
          notification.success(`${userObj.email} disabled`);
          fetchUsers();
        } catch (error) {
          notification.error(error.response?.data?.detail || 'Failed to disable user');
        }
      },
      { title: 'Disable User', confirmLabel: 'Disable', confirmClass: 'bg-amber-600 hover:bg-amber-700' }
    );
  };

  const handleEnable = async (userObj) => {
    try {
      await axios.post(`${API}/users/${userObj.id}/enable`, {}, { headers: getAuthHeader() });
      notification.success(`${userObj.email} re-enabled`);
      fetchUsers();
    } catch (error) {
      notification.error(error.response?.data?.detail || 'Failed to enable user');
    }
  };

  const handleResetPassword = (userObj) => {
    notification.confirm(
      `Send a password reset link to ${userObj.email}?\n\nThey will receive an email with instructions to set a new password.`,
      async () => {
        try {
          await axios.post(`${API}/users/${userObj.id}/reset-password`, {}, { headers: getAuthHeader() });
          notification.success(`Reset link sent to ${userObj.email}`);
        } catch (error) {
          notification.error(error.response?.data?.detail || 'Failed to send reset link');
        }
      },
      { title: 'Reset Password', confirmLabel: 'Send Reset Link', confirmClass: 'bg-blue-600 hover:bg-blue-700' }
    );
  };

  const getRoleBadge = (role) => {
    const variants = {
      'Super-User': 'bg-red-50 text-red-700 border-red-200',
      'Admin': 'bg-red-50 text-red-700 border-red-200',
      'Management': 'bg-purple-50 text-purple-700 border-purple-200',
      'Personnel DB Manager': 'bg-blue-50 text-blue-700 border-blue-200',
      'Equipment DB Manager': 'bg-blue-50 text-blue-700 border-blue-200',
      'Forecaster (Sales)': 'bg-amber-50 text-amber-700 border-amber-200',
      'Personnel Planner': 'bg-emerald-50 text-emerald-700 border-emerald-200',
      'Equipment Planner': 'bg-emerald-50 text-emerald-700 border-emerald-200',
      'Controlling': 'bg-slate-100 text-slate-700 border-slate-200',
      'Engineer': 'bg-cyan-50 text-cyan-700 border-cyan-200',
      'HR': 'bg-pink-50 text-pink-700 border-pink-200',
      'Travel Coordinator': 'bg-orange-50 text-orange-700 border-orange-200',
      'Maintenance Coordinator': 'bg-teal-50 text-teal-700 border-teal-200',
      'Project Admin': 'bg-indigo-50 text-indigo-700 border-indigo-200',
    };
    return variants[role] || 'bg-slate-100 text-slate-700 border-slate-200';
  };

  const getStatusBadge = (status) => {
    if (status === 'invited') {
      return 'bg-amber-50 text-amber-700 border-amber-200';
    }
    if (status === 'disabled') {
      return 'bg-slate-100 text-slate-500 border-slate-200';
    }
    return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  };

  const [activeTab, setActiveTab] = useState('users');

  const fetchSecurityLog = useCallback(async (evtFilter = 'all') => {
    setSecLogLoading(true);
    try {
      const q = evtFilter && evtFilter !== 'all' ? `?event=${evtFilter}&limit=300` : '?limit=300';
      const res = await axios.get(`${API}/audit/security-log${q}`, { headers: getAuthHeader() });
      setSecLog({ entries: res.data.entries || [], summary: res.data.summary || null });
    } catch (e) {
      notification.error(e.response?.data?.detail || 'Failed to load security log');
      setSecLog({ entries: [], summary: null });
    }
    setSecLogLoading(false);
  }, [notification]);

  useEffect(() => {
    if (activeTab === 'security-log' && isSuperUser) {
      fetchSecurityLog(secLogFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, secLogFilter, isSuperUser]);

  // Modules / matrix come from backend (matrixData.modules / matrixData.matrix)
  const MODULES = matrixData?.modules || [];

  // Convert backend level ("edit"|"view"|null) ↔ display ("E"|"V"|"-")
  const levelToDisplay = (lvl) => (lvl === 'edit' ? 'E' : lvl === 'view' ? 'V' : '-');
  const cycleLevel = (lvl) => (lvl === 'edit' ? 'view' : lvl === 'view' ? null : 'edit');

  const getCellLevel = (role, modKey, tab) => {
    const src = matrixDraft || matrixData?.matrix || {};
    const rolePerms = src[role] || {};
    const modAccess = rolePerms[modKey];
    if (tab) {
      if (modAccess && typeof modAccess === 'object') return modAccess[tab] ?? null;
      return null;
    }
    if (modAccess && typeof modAccess === 'object') return null;
    return modAccess ?? null;
  };

  const setCellLevel = (role, modKey, tab, newLevel) => {
    setMatrixDraft(prev => {
      const next = JSON.parse(JSON.stringify(prev || {}));
      if (!next[role]) next[role] = {};
      if (tab) {
        if (!next[role][modKey] || typeof next[role][modKey] !== 'object') {
          next[role][modKey] = {};
        }
        next[role][modKey][tab] = newLevel;
      } else {
        next[role][modKey] = newLevel;
      }
      return next;
    });
    setMatrixDirty(true);
  };

  const handleCellClick = (role, modKey, tab) => {
    if (!isSuperUser) return;
    const current = getCellLevel(role, modKey, tab);
    setCellLevel(role, modKey, tab, cycleLevel(current));
  };

  const handleSaveMatrix = async () => {
    setMatrixSaving(true);
    try {
      await axios.put(`${API}/access-matrix`, { matrix: matrixDraft }, { headers: getAuthHeader() });
      notification.success('Access Rights Matrix updated');
      await fetchAccessMatrix();
      if (refreshPermissions) await refreshPermissions();
    } catch (error) {
      notification.error(error.response?.data?.detail || 'Failed to save matrix');
    } finally {
      setMatrixSaving(false);
    }
  };

  const handleResetMatrix = () => {
    notification.confirm(
      'Reset the Access Rights Matrix to system defaults? This cannot be undone.',
      async () => {
        setMatrixSaving(true);
        try {
          await axios.post(`${API}/access-matrix/reset`, {}, { headers: getAuthHeader() });
          notification.success('Access Rights Matrix reset to defaults');
          await fetchAccessMatrix();
          if (refreshPermissions) await refreshPermissions();
        } catch (error) {
          notification.error(error.response?.data?.detail || 'Failed to reset matrix');
        } finally {
          setMatrixSaving(false);
        }
      },
      { title: 'Reset Matrix', confirmLabel: 'Reset', confirmClass: 'bg-amber-500 hover:bg-amber-600 text-white', iconColor: 'amber' }
    );
  };

  const handleCancelMatrix = () => {
    setMatrixDraft(JSON.parse(JSON.stringify(matrixData?.matrix || {})));
    setMatrixDirty(false);
  };

  const fetchAuditEntries = async () => {
    setAuditLoading(true);
    try {
      const res = await axios.get(`${API}/access-matrix/audit?limit=50`, { headers: getAuthHeader() });
      setAuditEntries(res.data.entries || []);
    } catch (error) {
      notification.error(error.response?.data?.detail || 'Failed to load change history');
    } finally {
      setAuditLoading(false);
    }
  };

  const handleOpenAudit = () => {
    setShowAuditDialog(true);
    fetchAuditEntries();
  };

  const formatLevel = (lvl) => {
    if (lvl === 'edit') return 'E';
    if (lvl === 'view') return 'V';
    return '–';
  };

  // Build user-role mapping for the matrix
  const userRoleMatrix = users.map(u => ({
    email: u.email,
    roles: ((u.roles && u.roles.length > 0 ? u.roles : [u.role]).filter(Boolean)),
  }));

  if (!canEdit('user_management')) {
    return (
      <div className="p-6 md:p-8 lg:p-12">
        <div className="text-center py-12">
          <p className="text-lg text-slate-600">Access denied. Insufficient permissions.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 lg:p-12" data-testid="users-page">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900">User Management</h1>
          <p className="text-sm text-slate-600 mt-2">Manage users, roles and access permissions</p>
        </div>
        <Button
          onClick={() => setShowCreateDialog(true)}
          data-testid="create-user-button"
          className="bg-amber-500 hover:bg-amber-600 text-white"
        >
          <Plus className="mr-2 h-4 w-4" />
          Invite User
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="users" data-testid="users-list-tab">
            <UsersIcon className="w-4 h-4 mr-1.5" />Users ({users.length})
          </TabsTrigger>
          <TabsTrigger value="matrix" data-testid="roles-matrix-tab">
            <Grid3X3 className="w-4 h-4 mr-1.5" />Roles Matrix
          </TabsTrigger>
          <TabsTrigger value="user-roles" data-testid="user-roles-tab">
            <Grid3X3 className="w-4 h-4 mr-1.5" />User-Role Map
          </TabsTrigger>
          {isSuperUser && (
            <TabsTrigger value="security-log" data-testid="security-log-tab">
              <ShieldCheck className="w-4 h-4 mr-1.5" />Security Log
            </TabsTrigger>
          )}
        </TabsList>

        {/* USERS LIST TAB */}
        <TabsContent value="users">
      <Card className="bg-white border-slate-200 shadow-sm" data-testid="users-table">
        <CardHeader>
          <CardTitle className="text-lg font-semibold tracking-tight text-slate-700 uppercase">System Users</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-mono text-xs uppercase">Email</TableHead>
                <TableHead className="font-mono text-xs uppercase">Role</TableHead>
                <TableHead className="font-mono text-xs uppercase">Status</TableHead>
                <TableHead className="font-mono text-xs uppercase">Created</TableHead>
                <TableHead className="font-mono text-xs uppercase w-[60px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                  <TableCell className="font-medium text-sm">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-slate-400" />
                      {u.email}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {((u.roles && u.roles.length > 0 ? u.roles : [u.role]).filter(Boolean)).map(r => r === 'Admin' ? 'Super-User' : r).map((r, i) => (
                        <Badge key={i} className={getRoleBadge(r)}>{r}</Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={getStatusBadge(u.status || 'active')}>
                      {u.status === 'invited' ? 'Pending Invite' : u.status === 'disabled' ? 'Disabled' : 'Active'}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono text-sm">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                  </TableCell>
                  <TableCell>
                    <div data-actions-menu>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0"
                            data-testid={`actions-menu-${u.id}`}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {(u.status === 'invited') && (
                            <DropdownMenuItem
                              onClick={() => handleResendInvite(u.id, u.email)}
                              data-testid={`resend-invite-${u.id}`}
                            >
                              <Send className="mr-2 h-4 w-4" />
                              Resend Invite
                            </DropdownMenuItem>
                          )}
                          {(u.status === 'active') && (
                            <DropdownMenuItem
                              onClick={() => handleResetPassword(u)}
                              data-testid={`reset-password-${u.id}`}
                            >
                              <KeyRound className="mr-2 h-4 w-4" />
                              Reset Password
                            </DropdownMenuItem>
                          )}
                          {(u.status === 'disabled') ? (
                            <DropdownMenuItem
                              onClick={() => handleEnable(u)}
                              data-testid={`enable-user-${u.id}`}
                              className="text-emerald-700 focus:text-emerald-700"
                            >
                              <ShieldCheck className="mr-2 h-4 w-4" />
                              Enable
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              onClick={() => handleDisable(u)}
                              data-testid={`disable-user-${u.id}`}
                              className="text-amber-700 focus:text-amber-700"
                              disabled={u.id === user?.id}
                            >
                              <Ban className="mr-2 h-4 w-4" />
                              Disable
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={() => handleDelete(u.id)}
                            data-testid={`delete-user-${u.id}`}
                            className="text-red-600 focus:text-red-600"
                            disabled={u.id === user?.id}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {users.length === 0 && (
            <p className="text-center text-slate-500 py-8 text-sm">No users found</p>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        {/* ROLES MATRIX TAB - Shows permissions per role */}
        <TabsContent value="matrix">
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <CardTitle className="text-lg font-semibold tracking-tight text-slate-700 uppercase">Access Rights Matrix</CardTitle>
                  {isSuperUser && (
                    <p className="text-xs text-slate-500 mt-1">Click any cell to cycle through Edit → View → No Access. Changes apply tenant-wide on Save.</p>
                  )}
                </div>
                <div className="flex items-center gap-4 text-xs">
                  <div className="flex items-center gap-1"><Pencil className="w-3 h-3 text-emerald-600" /><span className="text-slate-600">E = Edit</span></div>
                  <div className="flex items-center gap-1"><Eye className="w-3 h-3 text-blue-600" /><span className="text-slate-600">V = View</span></div>
                  <div className="flex items-center gap-1"><XIcon className="w-3 h-3 text-slate-300" /><span className="text-slate-600">- = No Access</span></div>
                </div>
              </div>
              {isSuperUser && (
                <div className="flex items-center gap-2 mt-3" data-testid="matrix-action-bar">
                  <Button
                    size="sm"
                    onClick={handleSaveMatrix}
                    disabled={!matrixDirty || matrixSaving}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
                    data-testid="save-matrix-button"
                  >
                    <Save className="w-3.5 h-3.5 mr-1.5" />
                    {matrixSaving ? 'Saving…' : 'Save Changes'}
                  </Button>
                  {matrixDirty && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleCancelMatrix}
                      disabled={matrixSaving}
                      data-testid="cancel-matrix-button"
                    >
                      Cancel
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleOpenAudit}
                    className="ml-auto"
                    data-testid="view-audit-button"
                  >
                    <History className="w-3.5 h-3.5 mr-1.5" />
                    Change History
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleResetMatrix}
                    disabled={matrixSaving}
                    className="text-amber-700 hover:text-amber-800 hover:bg-amber-50 border-amber-200"
                    data-testid="reset-matrix-button"
                  >
                    <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                    Reset to Defaults
                  </Button>
                  {matrixDirty && (
                    <span className="text-xs text-amber-600 font-medium" data-testid="matrix-dirty-indicator">Unsaved changes</span>
                  )}
                </div>
              )}
            </CardHeader>
            <CardContent>
              {!matrixData ? (
                <p className="text-center text-slate-500 py-8 text-sm">Loading matrix…</p>
              ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse min-w-[900px]">
                  <thead>
                    <tr className="bg-slate-50">
                      <th className="p-2 text-left font-semibold text-slate-600 border border-slate-200 sticky left-0 bg-slate-50 z-10 min-w-[150px]">Module / Tab</th>
                      {RBAC_ROLES.map(role => (
                        <th key={role} title={role} className="p-1.5 text-center font-semibold text-slate-600 border border-slate-200 min-w-[50px] max-w-[70px]">
                          <div className="writing-mode-vertical" style={{ writingMode: 'vertical-lr', transform: 'rotate(180deg)', whiteSpace: 'nowrap', height: '90px', display: 'flex', alignItems: 'center' }}>
                            {role}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {MODULES.map(mod => (
                      mod.tabs.length > 0 ? (
                        mod.tabs.map((tab, idx) => (
                          <tr key={`${mod.key}-${tab}`} className={idx === 0 ? 'border-t-2 border-slate-300' : ''}>
                            <td className="p-2 border border-slate-200 sticky left-0 bg-white z-10">
                              {idx === 0 && <span className="font-semibold text-slate-700">{mod.label}</span>}
                              <span className="text-slate-500 ml-1 capitalize">{tab}</span>
                            </td>
                            {RBAC_ROLES.map(role => {
                              const lvl = getCellLevel(role, mod.key, tab);
                              const display = levelToDisplay(lvl);
                              const cls = display === 'E' ? 'bg-emerald-50 text-emerald-700' : display === 'V' ? 'bg-blue-50 text-blue-700' : 'bg-slate-50 text-slate-300';
                              return (
                                <td
                                  key={role}
                                  data-testid={`matrix-cell-${role}-${mod.key}-${tab}`}
                                  onClick={() => handleCellClick(role, mod.key, tab)}
                                  className={`p-1.5 text-center border border-slate-200 font-mono font-bold ${cls} ${isSuperUser ? 'cursor-pointer hover:ring-2 hover:ring-emerald-300 hover:ring-inset' : ''}`}
                                >
                                  {display}
                                </td>
                              );
                            })}
                          </tr>
                        ))
                      ) : (
                        <tr key={mod.key} className="border-t-2 border-slate-300">
                          <td className="p-2 border border-slate-200 font-semibold text-slate-700 sticky left-0 bg-white z-10">{mod.label}</td>
                          {RBAC_ROLES.map(role => {
                            const lvl = getCellLevel(role, mod.key, null);
                            const display = levelToDisplay(lvl);
                            const cls = display === 'E' ? 'bg-emerald-50 text-emerald-700' : display === 'V' ? 'bg-blue-50 text-blue-700' : 'bg-slate-50 text-slate-300';
                            return (
                              <td
                                key={role}
                                data-testid={`matrix-cell-${role}-${mod.key}`}
                                onClick={() => handleCellClick(role, mod.key, null)}
                                className={`p-1.5 text-center border border-slate-200 font-mono font-bold ${cls} ${isSuperUser ? 'cursor-pointer hover:ring-2 hover:ring-emerald-300 hover:ring-inset' : ''}`}
                              >
                                {display}
                              </td>
                            );
                          })}
                        </tr>
                      )
                    ))}
                  </tbody>
                </table>
              </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* USER-ROLE MAP TAB - Shows which users have which roles */}
        <TabsContent value="user-roles">
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-semibold tracking-tight text-slate-700 uppercase">User-Role Assignments</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse min-w-[900px]">
                  <thead>
                    <tr className="bg-slate-50">
                      <th className="p-2 text-left font-semibold text-slate-600 border border-slate-200 sticky left-0 bg-slate-50 z-10 min-w-[200px]">User</th>
                      {RBAC_ROLES.map(role => (
                        <th key={role} className="p-1.5 text-center font-semibold text-slate-600 border border-slate-200 min-w-[50px] max-w-[70px]">
                          <div style={{ writingMode: 'vertical-lr', transform: 'rotate(180deg)', whiteSpace: 'nowrap', height: '90px', display: 'flex', alignItems: 'center' }}>
                            {role}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {userRoleMatrix.map(u => (
                      <tr key={u.email} className="hover:bg-slate-50">
                        <td className="p-2 border border-slate-200 font-medium text-slate-700 sticky left-0 bg-white z-10">{u.email}</td>
                        {RBAC_ROLES.map(role => {
                          const has = u.roles.includes(role) || (role === 'Super-User' && u.roles.includes('Admin'));
                          return (
                            <td key={role} className={`p-1.5 text-center border border-slate-200 ${has ? 'bg-emerald-50' : ''}`}>
                              {has && <Check className="w-4 h-4 text-emerald-600 mx-auto" />}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {isSuperUser && (
        <TabsContent value="security-log">
          <Card className="bg-white border-slate-200 shadow-sm" data-testid="security-log-panel">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold tracking-tight text-slate-700 uppercase flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-slate-500" />Security / Authentication Log
                </CardTitle>
                <p className="text-xs text-slate-500 mt-1">Login successes, failures and account lockouts for this tenant. Newest first.</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchSecurityLog(secLogFilter)}
                data-testid="security-log-refresh"
                className="h-8"
              >
                <RotateCcw className="w-4 h-4 mr-1.5" />Refresh
              </Button>
            </CardHeader>
            <CardContent>
              {/* Summary cards (last 24h) */}
              {secLog.summary && (
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3" data-testid="seclog-summary-success">
                    <div className="text-2xl font-bold text-emerald-700">{secLog.summary.login_success_24h}</div>
                    <div className="text-[11px] uppercase tracking-wide text-emerald-600">Successful logins (24h)</div>
                  </div>
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="seclog-summary-failed">
                    <div className="text-2xl font-bold text-amber-700">{secLog.summary.login_failed_24h}</div>
                    <div className="text-[11px] uppercase tracking-wide text-amber-600">Failed logins (24h)</div>
                  </div>
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3" data-testid="seclog-summary-locked">
                    <div className="text-2xl font-bold text-red-700">{secLog.summary.account_locked_24h}</div>
                    <div className="text-[11px] uppercase tracking-wide text-red-600">Account lockouts (24h)</div>
                  </div>
                </div>
              )}

              {/* Filter chips */}
              <div className="flex items-center gap-1.5 mb-3">
                {[
                  { k: 'all', label: 'All' },
                  { k: 'login_success', label: 'Success' },
                  { k: 'login_failed', label: 'Failed' },
                  { k: 'account_locked', label: 'Lockouts' },
                ].map(f => (
                  <Button
                    key={f.k}
                    variant="ghost"
                    size="sm"
                    onClick={() => setSecLogFilter(f.k)}
                    className={`h-7 px-3 text-xs ${secLogFilter === f.k ? 'bg-slate-900 text-white hover:bg-slate-800' : 'border border-slate-200'}`}
                    data-testid={`seclog-filter-${f.k}`}
                  >
                    {f.label}
                  </Button>
                ))}
                <span className="text-xs text-slate-400 ml-auto">{secLog.entries.length} shown</span>
              </div>

              {secLogLoading ? (
                <div className="text-center py-12 text-slate-400 text-sm">Loading…</div>
              ) : secLog.entries.length === 0 ? (
                <div className="text-center py-12 text-slate-400 text-sm" data-testid="seclog-empty">No security events found.</div>
              ) : (
                <ScrollArea className="h-[calc(100vh-420px)] min-h-[240px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[150px]">Event</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead className="w-[150px]">IP Address</TableHead>
                        <TableHead className="w-[200px]">Timestamp</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {secLog.entries.map((e, idx) => {
                        const badge = e.event === 'login_success'
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          : e.event === 'login_failed'
                          ? 'bg-amber-50 text-amber-700 border-amber-200'
                          : 'bg-red-50 text-red-700 border-red-200';
                        const label = e.event === 'login_success' ? 'Login Success'
                          : e.event === 'login_failed' ? 'Login Failed'
                          : 'Account Locked';
                        return (
                          <TableRow key={idx} data-testid={`seclog-row-${idx}`}>
                            <TableCell>
                              <Badge variant="outline" className={`text-xs ${badge}`}>{label}</Badge>
                            </TableCell>
                            <TableCell className="font-medium text-slate-700">{e.email || '—'}</TableCell>
                            <TableCell className="font-mono text-xs text-slate-500">{e.ip || '—'}</TableCell>
                            <TableCell className="text-xs text-slate-500">
                              {e.timestamp ? new Date(e.timestamp).toLocaleString('en-AU') : '—'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        )}
      </Tabs>

      {/* Access Matrix Change History Dialog */}
      <Dialog open={showAuditDialog} onOpenChange={setShowAuditDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="audit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-4 h-4" />
              Access Rights Matrix — Change History
            </DialogTitle>
            <DialogDescription>
              Last 50 changes to the Access Rights Matrix for this tenant. Most recent first.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="flex-1 max-h-[60vh] pr-3">
            {auditLoading ? (
              <p className="text-center text-sm text-slate-500 py-8">Loading…</p>
            ) : auditEntries.length === 0 ? (
              <p className="text-center text-sm text-slate-500 py-8">No changes recorded yet.</p>
            ) : (
              <div className="space-y-3" data-testid="audit-entries">
                {auditEntries.map((entry, idx) => (
                  <div key={entry.id || idx} className="border border-slate-200 rounded-md p-3 bg-slate-50/40">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2 text-sm">
                        <Badge className={entry.action === 'reset' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}>
                          {entry.action === 'reset' ? 'Reset to defaults' : 'Update'}
                        </Badge>
                        <span className="font-medium text-slate-700">{entry.user_email}</span>
                        <span className="text-slate-400">·</span>
                        <span className="text-slate-500 text-xs">
                          {entry.timestamp ? new Date(entry.timestamp).toLocaleString('en-AU') : ''}
                        </span>
                      </div>
                      <Badge variant="outline" className="text-[10px]">
                        {entry.change_count || 0} change{(entry.change_count || 0) === 1 ? '' : 's'}
                      </Badge>
                    </div>
                    {Array.isArray(entry.changes) && entry.changes.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-slate-500">
                              <th className="text-left font-medium py-1 pr-3">Role</th>
                              <th className="text-left font-medium py-1 pr-3">Module / Tab</th>
                              <th className="text-center font-medium py-1 pr-3">Before</th>
                              <th className="text-center font-medium py-1">After</th>
                            </tr>
                          </thead>
                          <tbody>
                            {entry.changes.slice(0, 25).map((c, ci) => (
                              <tr key={ci} className="border-t border-slate-200">
                                <td className="py-1 pr-3 font-medium text-slate-700">{c.role}</td>
                                <td className="py-1 pr-3 text-slate-600">
                                  <span className="capitalize">{(c.module || '').replace(/_/g, ' ')}</span>
                                  {c.tab && <span className="text-slate-400"> · <span className="capitalize">{c.tab}</span></span>}
                                </td>
                                <td className="py-1 pr-3 text-center font-mono">
                                  <span className={`inline-block px-1.5 py-0.5 rounded ${c.before === 'edit' ? 'bg-emerald-50 text-emerald-700' : c.before === 'view' ? 'bg-blue-50 text-blue-700' : 'bg-slate-100 text-slate-400'}`}>
                                    {formatLevel(c.before)}
                                  </span>
                                </td>
                                <td className="py-1 text-center font-mono">
                                  <span className={`inline-block px-1.5 py-0.5 rounded ${c.after === 'edit' ? 'bg-emerald-50 text-emerald-700' : c.after === 'view' ? 'bg-blue-50 text-blue-700' : 'bg-slate-100 text-slate-400'}`}>
                                    {formatLevel(c.after)}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {entry.changes.length > 25 && (
                          <p className="text-[10px] text-slate-400 text-center mt-1">
                            +{entry.changes.length - 25} more change{entry.changes.length - 25 === 1 ? '' : 's'} not shown
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 italic">No detailed changes recorded.</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAuditDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-md" data-testid="create-user-dialog">
          <DialogHeader>
            <DialogTitle>Invite New User</DialogTitle>
            <DialogDescription>
              Send an invitation email. The user will set their own secure password.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-email">Email Address</Label>
              <Input
                id="new-email"
                type="email"
                data-testid="new-email-input"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                required
                placeholder="user@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-role">Roles (select one or more)</Label>
              <div className="grid grid-cols-2 gap-1.5 max-h-48 overflow-auto p-2 border border-slate-200 rounded-md bg-white">
                {RBAC_ROLES.map(role => (
                  <label key={role} className="flex items-center gap-1.5 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer text-xs">
                    <input
                      type="checkbox"
                      checked={(newUser.roles || []).includes(role)}
                      onChange={(e) => {
                        const current = newUser.roles || [];
                        const updated = e.target.checked
                          ? [...current, role]
                          : current.filter(r => r !== role);
                        setNewUser({ ...newUser, roles: updated });
                      }}
                      className="rounded border-slate-300"
                    />
                    <span className="text-slate-700">{role}</span>
                  </label>
                ))}
              </div>
              {(newUser.roles || []).length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {(newUser.roles || []).map(r => (
                    <Badge key={r} className={`${getRoleBadge(r)} text-[10px]`}>{r}</Badge>
                  ))}
                </div>
              )}
            </div>
            <div className="bg-slate-50 rounded-md p-3">
              <p className="text-xs text-slate-500">
                An invitation email will be sent from <strong>horizon@equiprsoftware.com</strong> with a link to set up their account.
                The link expires in 72 hours.
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="confirm-create-user"
                className="bg-amber-500 hover:bg-amber-600 text-white"
                disabled={creating}
              >
                {creating ? 'Sending...' : 'Send Invitation'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
